import {
  HIGH_LEVEL_LABELS_TO_VALUES,
  METRIC_LABELS_TO_VALUES,
} from 'constants/metricLabelsToValues';
import {
  selectIsPredictiveGoalsFeatureFlagOn,
  selectIsSegmentAnalysisEmailOpenRateFeatureFlagOn,
} from 'core/userContext';
import {
  SEGMENT_METRIC_DEF_EXCEPTIONS,
  SEGMENT_METRIC_NAME_EXCEPTIONS,
} from 'modules/productAnalytics/constants';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { BannerVariants } from 'ui/banner/types';
import { AiColor16, WarningFill } from 'ui/icons';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { getUserFriendlyMetricLabel } from '../../utils';
import { StyledBanner, StyledLink, StyledWarningInfoBanner } from './Styled';
import { Segment } from './types';

type BannersProps = {
  selectedSegment: Segment;
  mediumMetrics: Record<'name', string>[];
  overallMetrics: Record<'enumName', string>[];
  unsupportedMetrics: string[];
  highCardinality?: string;
};

export const Banners = ({
  selectedSegment,
  mediumMetrics,
  overallMetrics,
  unsupportedMetrics = [],
  highCardinality,
}: BannersProps) => {
  const isAnalyticsBotFiltering = useFeatureFlag('AnalyticsBotFiltering');
  const isSegAnalysisEmailOpenRateFFOn = useSelector(
    selectIsSegmentAnalysisEmailOpenRateFeatureFlagOn,
  );

  // Predictive Audiences Banner
  const [showPredAudBanner, setShowPredAudBanner] = useState(
    !localStorage.getItem('hidePredAudBanner'),
  ); // only show banner if the user hasn't closed it before
  const predictiveAudiencesFFOn = useSelector(selectIsPredictiveGoalsFeatureFlagOn);

  const handleCloseBanner = () => {
    setShowPredAudBanner(false);
    localStorage.setItem('hidePredAudBanner', 'true'); // prevent banner from re-appearing in this session
  };

  // Unsupported Metrics/Metric Exception Banner (appears whenever Email Open Rate, Open Rate or an unsupported metric is selected)
  const allSelectedMetricNames = [
    ...mediumMetrics.map((metric) => metric.name),
    ...overallMetrics.map((metric) => metric.enumName),
  ];
  const selectedUnsupportedMetrics = allSelectedMetricNames
    .map((metricName) => getUserFriendlyMetricLabel(metricName))
    .filter((metricName) => unsupportedMetrics?.includes(metricName));
  const selectedMetricDefExceptions = SEGMENT_METRIC_NAME_EXCEPTIONS.filter((val) =>
    allSelectedMetricNames.includes(
      // @ts-ignore
      METRIC_LABELS_TO_VALUES(isAnalyticsBotFiltering)[val] || HIGH_LEVEL_LABELS_TO_VALUES[val],
    ),
  );

  const showUnsupportedMetricWarning = selectedUnsupportedMetrics?.length > 0;
  const showMetricDefWarning =
    isSegAnalysisEmailOpenRateFFOn && selectedMetricDefExceptions.length > 0;
  const showMetricBanner =
    selectedSegment && (showUnsupportedMetricWarning || showMetricDefWarning);

  const getBannerContent = () => {
    const unsupportedMetricsWarning = `${
      selectedUnsupportedMetrics.length
    } of the metrics you have selected cannot be displayed in the Segments table: 
  ${selectedUnsupportedMetrics.join(', ')}.`;
    const metricDefWarning = `For segment data, ${selectedMetricDefExceptions
      .map((val) => SEGMENT_METRIC_DEF_EXCEPTIONS[val].bannerName)
      .join(' and ')} ${
      selectedMetricDefExceptions.length > 1 ? 'are' : 'is'
    } calculated as Email Opens (one per messageId) / (Total Emails Delivered).`;
    return (
      <>
        {showUnsupportedMetricWarning && unsupportedMetricsWarning}
        {showUnsupportedMetricWarning && showMetricDefWarning && <br />}
        {showMetricDefWarning && metricDefWarning}
      </>
    );
  };

  return (
    <>
      {predictiveAudiencesFFOn && showPredAudBanner && (
        <StyledBanner
          data-test={'predictive-goals-banner'}
          Icon={<AiColor16 />}
          onClose={handleCloseBanner}
        >
          Use <StyledLink href="/predictive-audiences">Predictive Goals</StyledLink> to help
          identify other potential segments who are likely to convert.
        </StyledBanner>
      )}
      {showMetricBanner && (
        <StyledBanner data-test={'unsupported-metrics-banner'}>{getBannerContent()}</StyledBanner>
      )}
      {highCardinality && (
        <StyledWarningInfoBanner
          data-test={'high-cardinality-banner'}
          variant={BannerVariants.WARNING}
          Icon={<WarningFill height={16} width={16} />}
        >
          The <b>{highCardinality}</b> segment can only display the first 250 records.
        </StyledWarningInfoBanner>
      )}
    </>
  );
};
